import { Select, Box } from "@mui/material";
import { FC, useContext, useEffect, useState } from "react";
import propTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { api, AssetLocation, IKmosGetLanesParameters, KmosLaneFilter } from "../../../../logic/api";
import { SessionContext } from "../../../../utility/useSession";
import FloatingInputLabel from "../../../../components/editable/EditableField/components/FloatingInputLabel";
import { ProgressView } from "../../../../components/notifications/progress/ProgressView";
import { usePreferences } from "../../../../utility/usePreferences";

enum LocationsGroupBy {
  All = 0,
  Allocated = 1,
  UnAllocated = 2,
}

const KmosLaneSelector: FC<{
  label: string,
  value?: AssetLocation | null;
  params: IKmosGetLanesParameters;
  filter: KmosLaneFilter;
  splitByAllocated?: boolean;
  onChange: (value: AssetLocation) => void;
}> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const setParams = useState<IKmosGetLanesParameters>({ ...props.params, laneFilter: props.filter })[1];
  const { kmosVersion } = usePreferences();
  const locations = api.hooks.kmosV2.getLanes();

  useEffect(() => {
    const newParams = { ...props.params, laneFilter: props.filter };

    setParams(newParams);
    locations.send(session.current.context, newParams);

  }, [props.filter, props.params]);

  const mapAndSortLocations = (locationsData: AssetLocation[], groupBy: LocationsGroupBy) => {
    const filteredLocations = locationsData.filter(loc => {
      if (groupBy === LocationsGroupBy.All) return true;

      if (groupBy === LocationsGroupBy.Allocated) {
        if (loc.hasOwnProperty("allocationStatus")) {
          return loc.allocationStatus === t("kmos.labels.allocated");
        }

        return (loc.allocationCount || loc.allocations);
      }

      if (groupBy === LocationsGroupBy.UnAllocated) {
        if (loc.hasOwnProperty("allocationStatus")) {
          return loc.allocationStatus === t("kmos.labels.unallocated");
        }

        return (!loc.allocationCount && !loc.allocations);
      }
    });

    const sortedFilteredLocations = filteredLocations.sort((locA, locB) => locA.parentName?.localeCompare(locB.parentName) || locA.name.localeCompare(locB.name));

    return sortedFilteredLocations.map((locData, i) => laneOption(locData, i));
  }

  const laneOption = ({ id, name, parentName, spaces }: AssetLocation, index: number) => {
    return <option key={index} value={id} title={name}>
      {t("kmos.labels.laneOption", { parentName, name, spaces })}
    </option>
  }

  let selectedLocation: AssetLocation | null = null;

  if (props.value) {
    selectedLocation = props.value;
  }

  const unAllocatedLocations = kmosVersion === "2.0" && props.splitByAllocated && locations.data
    ? mapAndSortLocations(locations.data, LocationsGroupBy.UnAllocated)
    : [];
  const allocatedLocations = kmosVersion === "2.0" && props.splitByAllocated && locations.data
    ? mapAndSortLocations(locations.data, LocationsGroupBy.Allocated)
    : [];
  const allLocations = (!props.splitByAllocated || kmosVersion === "1.0") && locations.data
    ? mapAndSortLocations(locations.data, LocationsGroupBy.All)
    : [];

  return (
    <Box sx={{ display: "flex", width: 200, mx: 'auto' }}>
      <ProgressView status={locations.status}>
        <FloatingInputLabel label={props.label}>
            <Select
                native
                style={{ width: "100%", maxWidth: 225, borderRadius: 5, padding: 5 }}

                value={!selectedLocation ? -1 : selectedLocation.id}
                onChange={(e) => {
                    if (locations.data) {
                        const matched = locations.data.filter(l => l.id == e.target.value);

                        if (matched.length > 0) {
                            props.onChange(matched[0]);
                            return;
                        }
                    }
                }}
            >
                <option value={-1} title={""}></option>
                {kmosVersion === "2.0" && props.splitByAllocated && locations.data && <>
                    {unAllocatedLocations.length &&
                        <optgroup label={t("kmos.labels.unallocated")}>
                            {unAllocatedLocations}
                        </optgroup>
                    }
                    {allocatedLocations.length &&
                        <optgroup label={t("kmos.labels.allocated")}>
                            {allocatedLocations}
                        </optgroup>
                    }
                </>}

                {(!props.splitByAllocated || kmosVersion === "1.0") && allLocations.length && allLocations}
            </Select>
        </FloatingInputLabel>
      </ProgressView>
    </Box>
  );
}

KmosLaneSelector.propTypes = {
  label: propTypes.string.isRequired,
  value: propTypes.any,
  onChange: propTypes.func.isRequired,
  params: propTypes.any.isRequired,
  filter: propTypes.any.isRequired
}

export default KmosLaneSelector;
